import { LinkCellRenderer } from "@/controls/ag-grid/components/ButtonCellRenderer";
import { routeDelaysManager } from "@/setup/routes/delay.routes";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { ColDef, ColGroupDef, ICellRendererParams } from "ag-grid-enterprise";
import moment from "moment";
import { GridButtons } from "../components/GridButtons";
import { CommentsColumn } from "../components/table/CommentColumn";
import { MillAdjustmentByShift } from "../models/MillAdjustmentByShift";

function formatDecimal(value: number): string {
	if (Number.isInteger(value)) {
		return value.toString();
	}

	const fixedValue = value.toFixed(3);
	const trimmedValue = fixedValue.replace(/(\.0*|0*)$/, "");

	return trimmedValue;
}

type Props = {
	editData: (data: MillAdjustmentByShift) => void;
	deleteData: (data: MillAdjustmentByShift) => void;
	showContextMenu: Function;
};

export const useTableData = ({
	editData,
	deleteData,
	showContextMenu,
}: Props) => {
	const columnDefs: (ColDef | ColGroupDef)[] = [
		{
			headerName: "Adjustment",
			children: [
				{
					field: "Timestamp",
					headerName: "Time",
					valueGetter: (params) =>
						dxtToLocalServerTime(
							(params.data as MillAdjustmentByShift)?.Timestamp,
							"MM/dd HH:mm:ss",
						) || "",
					maxWidth: 120,
					flex: 1,
				},
				{
					field: "Stands",
					headerName: "Stands",
					maxWidth: 90,
					editable: false,
					flex: 1,
				},
				{
					field: "OperatorName",
					headerName: "Operator",
					editable: false,
					flex: 1,
				},
				{
					field: "AdjustmentTypeName",
					headerName: "Type",
					editable: false,
					flex: 2,
					cellStyle: {
						justifyContent: "flex-start",
					},
					wrapText: true,
					autoHeight: true,
				},
				{
					field: "AdjustmentReasonName",
					headerName: "Reason",
					editable: false,
					flex: 2,
				},
				{
					field: "AdjustmentValue",
					headerName: "Value",
					maxWidth: 100,
					editable: false,
					valueGetter: (params) => {
						const value = (params.data as MillAdjustmentByShift)
							.AdjustmentValue;
						const unit = (params.data as MillAdjustmentByShift).Unit;

						if (value === null || value === undefined) {
							return "";
						}

						const numValue = Number(value);
						const formattedValue = formatDecimal(numValue);

						return `${formattedValue} ${unit && unit !== "-" ? unit : ""}`;
					},
					flex: 1,
				},
				{
					field: "Comments",
					headerName: "",
					maxWidth: 75,
					flex: 1,
					cellRenderer: (params: any) => {
						return CommentsColumn({ data: params.data });
					},
					valueGetter: (params) => params.data?.Comments || "",
				},
			],
		},
		{
			headerName: "Delay",
			headerClass: "start-column",
			children: [
				{
					field: "Start",
					headerName: "Start",
					cellRenderer: (params: any) => {
						const shiftId = (params.data as MillAdjustmentByShift).ShiftId;
						const delayId = (params.data as MillAdjustmentByShift).DelayId;

						return LinkCellRenderer({
							...params,
							link: `${routeDelaysManager}/${shiftId}?assetId=12&delayId=${delayId}`,
						});
					},
					valueGetter: (params) =>
						dxtToLocalServerTime(
							(params.data as MillAdjustmentByShift)?.Start,
							"MM/dd HH:mm:ss",
						) || "",
					maxWidth: 120,
					flex: 1,
					headerClass: "start-column",
				},
				{
					field: "End",
					headerName: "End",
					valueGetter: (params) =>
						dxtToLocalServerTime(
							(params.data as MillAdjustmentByShift)?.End,
							"MM/dd HH:mm:ss",
						) || "",
					maxWidth: 120,
					flex: 1,
				},
				{
					field: "Duration",
					headerName: "Duration",
					valueFormatter: (params) => {
						if (params.value) {
							const duration = moment.duration(params.value, "seconds");
							const minutes = Math.floor(duration.asMinutes());
							const seconds = duration.seconds();
							return `${minutes}m ${seconds}s`;
						}
						return "";
					},
					editable: false,
					flex: 1,
				},
			],
		},
		{
			cellRenderer: GridButtons,
			cellRendererParams: (params: ICellRendererParams) => ({
				data: params.data as MillAdjustmentByShift,
				editData: editData,
				deleteData: deleteData,
				showContextMenu: () =>
					showContextMenu(
						event,
						params.data as MillAdjustmentByShift,
						"adjustmentManagerContext",
					),
			}),
			wrapText: false,
			wrapHeaderText: false,
			headerName: "",
			flex: 1,
			headerClass: "start-column",
		},
	];
	return { columnDefs };
};
